import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as CryptoJS from 'crypto-js';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class FacebookPixelService {
  private pixelId = '1068810562044559';

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      if (this.pixelId) {
        this.loadPixel();
      }
    }
  }

  private loadPixel(): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') return;

    if (window.fbq) {
      return;
    }

    // GDPR: Check local consent before loading pixel
    const granularRaw = localStorage.getItem('cookieConsentGranular');
    const simpleConsent = localStorage.getItem('cookieConsent');

    let marketingGranted = false;

    if (granularRaw) {
      try {
        const granular = JSON.parse(granularRaw);
        marketingGranted = granular.marketing === true;
      } catch (e) {}
    } else {
      // Fallback for legacy/simple All-In consent
      marketingGranted = simpleConsent === 'granted';
    }

    if (!marketingGranted) {
      // console.warn('[Meta Pixel] Marketing usage denied. Pixel not loaded.');
      return;
    }

    (function (f: any, b, e, v, n?, t?, s?) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', this.pixelId);
    window.fbq('track', 'PageView');
  }

  public track(eventName: string, params?: object): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') return;
    try {
      if (window.fbq && this.pixelId) {
        window.fbq('track', eventName, params);
      } else {
        // console.warn(`[Meta Pixel] fbq não disponível ou Pixel ID ausente. Evento '${eventName}' não enviado`);
      }
    } catch (error) {
      //   console.error(`[Meta Pixel] Erro ao enviar evento '${eventName}':`, error);
    }
  }

  private hashSHA256(input: string): string {
    if (!input) return '';
    return CryptoJS.SHA256(input.trim().toLowerCase()).toString(CryptoJS.enc.Hex);
  }

  public trackHashedEvent(
    eventName: string,
    data: {
      email?: string;
      phone?: string;
      nif?: string;
      extraParams?: object;
    },
  ): void {
    try {
      const hashedParams = {
        em: this.hashSHA256(data.email || ''),
        ph: this.hashSHA256(data.phone || ''),
        external_id: this.hashSHA256(data.nif || ''),
        ...(data.extraParams || {}),
      };

      this.track(eventName, hashedParams);
    } catch (error) {
      //   console.error(`[Meta Pixel] Erro ao enviar evento '${eventName}' com hash:`, error);
    }
  }

  /**
   * Track ViewContent - Visualização de produto/seguro
   * @param contentName Nome do produto (ex: "Seguro Automóvel")
   * @param contentId ID do produto (ex: "prod_1", "seguro_auto")
   * @param value Valor estimado (opcional)
   */
  public trackViewContent(contentName: string, contentId: string, value: number = 0): void {
    this.track('ViewContent', {
      content_type: 'product',
      content_name: contentName,
      content_ids: [contentId],
      content_category: 'insurance',
      value: value,
      currency: 'EUR',
    });
  }

  /**
   * Track AddToCart - Utilizador inicia simulação (equivalente a "adicionar ao carrinho")
   * @param contentName Nome do produto
   * @param contentId ID da simulação
   * @param value Valor da simulação se disponível
   */
  public trackAddToCart(contentName: string, contentId: string, value: number = 0): void {
    this.track('AddToCart', {
      content_type: 'product',
      content_name: contentName,
      content_ids: [contentId],
      value: value,
      currency: 'EUR',
    });
  }

  /**
   * Track Purchase - Apólice subscrita/comprada
   * @param value Prémio da apólice
   * @param contentId ID da apólice
   * @param contentName Número da apólice ou nome
   */
  public trackPurchase(value: number, contentId: string, contentName: string): void {
    this.track('Purchase', {
      value: value,
      currency: 'EUR',
      content_type: 'insurance_policy',
      content_ids: [contentId],
      content_name: contentName,
      num_items: 1,
    });
  }

  /**
   * Track Search - Pesquisa de produtos/seguros
   * @param searchString Termo pesquisado
   */
  public trackSearch(searchString: string): void {
    if (searchString && searchString.length > 2) {
      this.track('Search', {
        search_string: searchString,
        content_category: 'insurance',
      });
    }
  }

  /**
   * Track Contact - Pedido de contacto
   * @param method Método de contacto (form, chat, phone)
   */
  public trackContact(method: string = 'form'): void {
    this.track('Contact', {
      content_category: 'insurance',
      method: method,
    });
  }

  /**
   * Track CompleteRegistration - Registo de utilizador concluído
   */
  public trackCompleteRegistration(params?: object): void {
    this.track('CompleteRegistration', params);
  }

  /**
   * Track Custom Event
   * @param eventName Nome do evento personalizado
   * @param params Parâmetros adicionais
   */
  public trackCustomEvent(eventName: string, params?: object): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') return;
    try {
      if (window.fbq && this.pixelId) {
        window.fbq('trackCustom', eventName, params);
      }
    } catch (error) {
      // Silent fail
    }
  }
}
